import { useState, useEffect, useMemo } from "react";
import { Search, Package, ChevronRight, ChevronLeft, Check, X, AlertTriangle, ClipboardList } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { ToggleSwitch as Switch } from "../ui/toggle-switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { cn } from "../../lib/utils";
import { calcolaCostoIngrediente, udmInputLabel } from "../../utils/udmUtils";

/**
 * Wizard per creare una nuova ricetta in 3 step:
 * 1. Seleziona ingredienti (checkbox + ricerca)
 * 2. Inserisci quantità per ogni ingrediente
 * 3. Nome, categoria, tracciabilità e salva
 */
const NuovaRicettaWizard = ({
  open,
  onOpenChange,
  ingredienti = [],
  categorie = [],
  categorieIngredienti = [],
  onSave
}) => {
  const [step, setStep] = useState(1);
  const [search, setSearch] = useState("");
  
  // Step 1: Ingredienti selezionati (array di ID)
  const [selectedIds, setSelectedIds] = useState([]);
  
  // Step 2: Quantità per ogni ingrediente { id: quantita }
  const [quantita, setQuantita] = useState({});
  
  // Step 3: Dati ricetta
  const [nome, setNome] = useState("");
  const [categoriaId, setCategoriaId] = useState("");
  const [richiedeTracciabilita, setRichiedeTracciabilita] = useState(false);
  
  // Reset quando si chiude
  useEffect(() => {
    if (!open) {
      setStep(1);
      setSearch("");
      setSelectedIds([]);
      setQuantita({});
      setNome("");
      setCategoriaId("");
      setRichiedeTracciabilita(false);
    }
  }, [open]);

  // Mappa categorie ingredienti
  const categorieMap = useMemo(() => {
    const map = {};
    categorieIngredienti.forEach(c => { map[c.id] = c.nome; });
    return map;
  }, [categorieIngredienti]);

  // Filtra ingredienti per ricerca
  const ingredientiFiltrati = useMemo(() => {
    if (!search.trim()) return ingredienti;
    const s = search.toLowerCase();
    return ingredienti.filter(i => i.nome?.toLowerCase().includes(s));
  }, [ingredienti, search]);

  // Raggruppa per categoria
  const ingredientiGrouped = useMemo(() => {
    const groups = {};
    ingredientiFiltrati.forEach(i => {
      const cat = categorieMap[i.categoria_id] || "Senza categoria";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(i);
    });
    // Ordina gruppi e ingredienti
    const sortedKeys = Object.keys(groups).sort((a, b) => {
      if (a === "Senza categoria") return 1;
      if (b === "Senza categoria") return -1;
      return a.localeCompare(b, 'it');
    });
    sortedKeys.forEach(k => {
      groups[k].sort((a, b) => a.nome.localeCompare(b.nome, 'it'));
    });
    return { groups, sortedKeys };
  }, [ingredientiFiltrati, categorieMap]);

  // Toggle selezione ingrediente
  const toggleIngrediente = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(x => x !== id)
        : [...prev, id]
    );
  };

  // Ingredienti selezionati con dati completi
  const ingredientiSelezionati = useMemo(() => {
    return selectedIds
      .map(id => ingredienti.find(i => i.id === id))
      .filter(Boolean);
  }, [selectedIds, ingredienti]);

  // Calcola costo totale stimato
  const costoTotaleStimato = useMemo(() => {
    let totale = 0;
    ingredientiSelezionati.forEach(ing => {
      const qty = parseFloat(quantita[ing.id]) || 0;
      const prezzo = ing.prezzo_attuale || 0;
      totale += calcolaCostoIngrediente(qty, prezzo, ing.unita_misura);
    });
    return totale;
  }, [ingredientiSelezionati, quantita]);

  // Validazione step
  const canGoToStep2 = selectedIds.length > 0 && nome.trim().length > 0;
  const canGoToStep3 = ingredientiSelezionati.every(ing => {
    const qty = parseFloat(quantita[ing.id]);
    return qty && qty > 0;
  });
  const canSave = true; // Nome già validato nello step 1

  // Salva ricetta
  const handleSave = () => {
    const ingredientiData = ingredientiSelezionati.map(ing => {
      const qty = parseFloat(quantita[ing.id]) || 0;
      const prezzo = ing.prezzo_attuale || 0;
      const costoIng = calcolaCostoIngrediente(qty, prezzo, ing.unita_misura);
      
      return {
        ingrediente_id: ing.id,
        ingrediente_nome: ing.nome,
        quantita: qty,
        unita_misura: ing.unita_misura || "kg",
        cotto: false,
        calo_cottura_percentuale: 0,
        is_acqua: false,
        prezzo_usato: prezzo,
        costo_ingrediente: Math.round(costoIng * 100) / 100
      };
    });

    onSave({
      nome,
      categoria_id: categoriaId || null,
      ingredienti: ingredientiData,
      richiede_tracciabilita: richiedeTracciabilita,
      tempo_preparazione: null,
      procedimento: [],
      note: null
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col p-0 gap-0" data-testid="new-recipe-wizard-dialog">
        {/* Header con step indicator */}
        <DialogHeader className="p-4 pb-2 border-b">
          <DialogTitle className="text-lg" data-testid="new-recipe-wizard-title">Nuova Ricetta</DialogTitle>
          <DialogDescription className="sr-only">Crea una ricetta selezionando ingredienti, quantità e impostazioni finali.</DialogDescription>
          <div className="flex items-center gap-2 mt-2">
            {[1, 2, 3].map(s => (
              <div key={s} className="flex items-center">
                <div className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors",
                  step === s ? "bg-primary text-white" : 
                  step > s ? "bg-green-500 text-white" : "bg-muted text-muted-foreground"
                )}>
                  {step > s ? <Check className="h-4 w-4" /> : s}
                </div>
                {s < 3 && (
                  <div className={cn(
                    "w-8 h-0.5 mx-1",
                    step > s ? "bg-green-500" : "bg-muted"
                  )} />
                )}
              </div>
            ))}
            <span className="ml-2 text-sm text-muted-foreground">
              {step === 1 && "Seleziona ingredienti"}
              {step === 2 && "Quantità"}
              {step === 3 && "Salva"}
            </span>
          </div>
        </DialogHeader>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* STEP 1: Nome ricetta + Selezione ingredienti */}
          {step === 1 && (
            <div className="space-y-4">
              {/* Nome ricetta - PRIMO CAMPO */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Nome Ricetta *</Label>
                <Input
                  placeholder="es. Salsa Quattro Formaggi"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  className="text-lg font-medium"
                  data-testid="new-recipe-name-input"
                />
              </div>

              {/* Ricerca ingredienti */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Cerca ingrediente..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="new-recipe-ingredient-search"
                />
              </div>

              {/* Contatore selezione */}
              {selectedIds.length > 0 && (
                <div className="flex items-center justify-between bg-primary/10 text-primary rounded-lg px-3 py-2 text-sm">
                  <span>{selectedIds.length} ingredienti selezionati</span>
                  <button 
                    onClick={() => setSelectedIds([])}
                    className="text-xs hover:underline"
                    data-testid="new-recipe-clear-ingredients"
                  >
                    Deseleziona tutti
                  </button>
                </div>
              )}

              {/* Lista ingredienti raggruppata */}
              <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                {ingredientiGrouped.sortedKeys.map(gruppo => (
                  <div key={gruppo}>
                    {/* Header categoria */}
                    <div className="sticky top-0 bg-muted/80 backdrop-blur-sm px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase flex items-center gap-2">
                      <Package className="h-3 w-3" />
                      {gruppo}
                      <span className="font-normal">({ingredientiGrouped.groups[gruppo].length})</span>
                    </div>
                    
                    {/* Ingredienti */}
                    {ingredientiGrouped.groups[gruppo].map(ing => (
                      <button
                        key={ing.id}
                        onClick={() => toggleIngrediente(ing.id)}
                        className={cn(
                          "w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors",
                          selectedIds.includes(ing.id) 
                            ? "bg-primary/15 border border-primary/30" 
                            : "hover:bg-muted/50"
                        )}
                        data-testid={`new-recipe-ingredient-${ing.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            "w-5 h-5 rounded border-2 flex items-center justify-center transition-colors",
                            selectedIds.includes(ing.id)
                              ? "bg-primary border-primary"
                              : "border-muted-foreground/30"
                          )}>
                            {selectedIds.includes(ing.id) && (
                              <Check className="h-3 w-3 text-white" />
                            )}
                          </div>
                          <span className="font-medium">{ing.nome}</span>
                        </div>
                        <span className="text-sm text-muted-foreground">
                          €{(ing.prezzo_attuale || 0).toFixed(2)}/{ing.unita_misura || 'kg'}
                        </span>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* STEP 2: Quantità */}
          {step === 2 && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Inserisci la quantità nell’unità indicata per ogni ingrediente
              </p>
              
              <div className="space-y-2">
                {ingredientiSelezionati.map(ing => (
                  <div 
                    key={ing.id}
                    className="flex items-center gap-3 bg-card border rounded-xl p-3"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{ing.nome}</p>
                      <p className="text-xs text-muted-foreground">
                        €{(ing.prezzo_attuale || 0).toFixed(2)}/{ing.unita_misura || 'kg'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="0"
                        step="any"
                        placeholder="0"
                        value={quantita[ing.id] || ""}
                        onChange={(e) => setQuantita(prev => ({
                          ...prev,
                          [ing.id]: e.target.value
                        }))}
                        className="w-24 text-right"
                        data-testid={`new-recipe-quantity-${ing.id}`}
                      />
                      <span className="min-w-8 text-sm text-muted-foreground" data-testid={`new-recipe-unit-${ing.id}`}>
                        {udmInputLabel(ing.unita_misura)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Costo stimato */}
              <div className="bg-muted/50 rounded-xl p-4 mt-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Costo ingredienti stimato:</span>
                  <span className="text-lg font-bold" data-testid="new-recipe-estimated-cost">€{costoTotaleStimato.toFixed(2)}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Il costo/kg sarà calcolato dopo la verifica del peso finale
                </p>
              </div>
            </div>
          )}

          {/* STEP 3: Salva */}
          {step === 3 && (
            <div className="space-y-4">
              {/* Nome ricetta - Solo visualizzazione */}
              <div className="bg-primary/10 rounded-xl p-4">
                <p className="text-xs text-muted-foreground">Nome Ricetta</p>
                <p className="text-xl font-bold text-foreground">{nome}</p>
              </div>

              {/* Categoria */}
              <div className="space-y-2">
                <Label>Categoria</Label>
                <Select value={categoriaId} onValueChange={setCategoriaId}>
                  <SelectTrigger data-testid="new-recipe-category-select">
                    <SelectValue placeholder="Seleziona categoria..." />
                  </SelectTrigger>
                  <SelectContent>
                    {categorie.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Tracciabilità */}
              <div className="flex items-center justify-between bg-card border rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <ClipboardList className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">Tracciabilità Lotti</p>
                    <p className="text-xs text-muted-foreground">
                      Attiva se questa ricetta deve apparire nella sezione tracciabilità
                    </p>
                  </div>
                </div>
                <Switch
                  checked={richiedeTracciabilita}
                  onCheckedChange={setRichiedeTracciabilita}
                  data-testid="new-recipe-traceability-switch"
                />
              </div>

              {/* Riepilogo ingredienti */}
              <div className="space-y-2">
                <Label className="text-muted-foreground">Riepilogo ({ingredientiSelezionati.length} ingredienti)</Label>
                <div className="bg-muted/30 rounded-xl p-3 max-h-32 overflow-y-auto">
                  {ingredientiSelezionati.map(ing => (
                    <div key={ing.id} className="flex justify-between text-sm py-1" data-testid={`new-recipe-summary-${ing.id}`}>
                      <span>{ing.nome}</span>
                      <span className="text-muted-foreground" data-testid={`new-recipe-summary-quantity-${ing.id}`}>
                        {quantita[ing.id]} {udmInputLabel(ing.unita_misura)}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between font-medium pt-2 border-t">
                  <span>Costo totale stimato</span>
                  <span data-testid="new-recipe-summary-cost">€{costoTotaleStimato.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer con navigazione */}
        <div className="p-4 border-t flex gap-2">
          {step > 1 && (
            <Button
              variant="outline"
              onClick={() => setStep(step - 1)}
              className="flex-1"
              data-testid="new-recipe-back-button"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Indietro
            </Button>
          )}
          
          {step === 1 && (
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
              data-testid="new-recipe-cancel-button"
            >
              Annulla
            </Button>
          )}
          
          {step < 3 && (
            <Button
              onClick={() => setStep(step + 1)}
              disabled={step === 1 ? !canGoToStep2 : !canGoToStep3}
              className="flex-1"
              data-testid="new-recipe-next-button"
            >
              Avanti
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          )}
          
          {step === 3 && (
            <Button
              onClick={handleSave}
              disabled={!canSave}
              className="flex-1 bg-primary"
              data-testid="new-recipe-submit-button"
            >
              <Check className="h-4 w-4 mr-1" />
              Crea Ricetta
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NuovaRicettaWizard;
